import { z } from "zod";
import { businessRoleSchema } from "../enums.ts";
import {
  SUPPORT_LIMITS,
  supportColorSchema,
  supportContextSchema,
  supportFileTypeSchema,
  supportKindSchema,
  supportPrioritySchema,
  supportRatingSchema,
  supportUrgencySchema,
} from "../support.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * Support tickets (strategy repo: teamna/strategy/support-system.md). Desk routes live under
 * `/v1/support/businesses/:businessId/...`, Control Center routes under `/v1/admin/support/...`.
 */

const subjectSchema = z.string().trim().min(1).max(SUPPORT_LIMITS.subject);
const bodySchema = z.string().trim().min(1).max(SUPPORT_LIMITS.body);
const fileIdsSchema = z.array(uuidSchema).max(SUPPORT_LIMITS.filesPerMessage).default([]);

export const supportTicketParamSchema = z.object({ businessId: uuidSchema, ticketId: uuidSchema });

// --- Desk: the sender -----------------------------------------------------------------------

export const supportFileSchema = z.object({
  id: uuidSchema,
  fileName: z.string(),
  contentType: supportFileTypeSchema,
  byteSize: z.number().int(),
  /** A signed address that expires; null when the file is gone. */
  url: z.string().nullable(),
});
export type SupportFile = z.infer<typeof supportFileSchema>;

export const uploadSupportFileRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(SUPPORT_LIMITS.fileName),
  contentType: supportFileTypeSchema,
  /** The file as base64, no data URL prefix. */
  data: z.string().min(1).max(Math.ceil((SUPPORT_LIMITS.fileBytes * 4) / 3) + 4),
});
export type UploadSupportFileRequest = z.infer<typeof uploadSupportFileRequestSchema>;

export const supportTicketSummarySchema = z.object({
  id: uuidSchema,
  number: z.number().int(),
  businessId: uuidSchema,
  subject: z.string(),
  state: supportKindSchema,
  urgency: supportUrgencySchema,
  createdAt: isoDateTimeSchema,
  lastActivityAt: isoDateTimeSchema,
  /** A team message the reader has not opened yet. */
  unread: z.boolean(),
  /** True when the reader sent it. */
  mine: z.boolean(),
  sentBy: z.object({ name: z.string().nullable(), role: businessRoleSchema }),
});
export type SupportTicketSummary = z.infer<typeof supportTicketSummarySchema>;

export const supportTicketsResponseSchema = z.object({ items: z.array(supportTicketSummarySchema) });
export type SupportTicketsResponse = z.infer<typeof supportTicketsResponseSchema>;

export const supportThreadMessageSchema = z.object({
  id: uuidSchema,
  /** `customer` is somebody at the business, `team` is Teamna, `system` is a line such as "Closed after 7 days". */
  author: z.enum(["customer", "team", "system"]),
  /** The first name of the person who wrote it. For the team, the team member's first name. */
  authorName: z.string().nullable(),
  mine: z.boolean(),
  body: z.string(),
  createdAt: isoDateTimeSchema,
  files: z.array(supportFileSchema),
});
export type SupportThreadMessage = z.infer<typeof supportThreadMessageSchema>;

export const supportTicketDetailSchema = supportTicketSummarySchema.extend({
  messages: z.array(supportThreadMessageSchema),
  rating: supportRatingSchema.nullable(),
});
export type SupportTicketDetail = z.infer<typeof supportTicketDetailSchema>;

export const createSupportTicketRequestSchema = z.object({
  subject: subjectSchema,
  body: bodySchema,
  urgency: supportUrgencySchema.default("normal"),
  fileIds: fileIdsSchema,
  context: supportContextSchema.default({}),
});
export type CreateSupportTicketRequest = z.input<typeof createSupportTicketRequestSchema>;

export const supportReplyRequestSchema = z.object({ body: bodySchema, fileIds: fileIdsSchema });
export type SupportReplyRequest = z.input<typeof supportReplyRequestSchema>;

/** "Did this solve it?" on a resolved request. Once per resolve: a reopen clears it. */
export const supportRatingRequestSchema = z.object({ rating: supportRatingSchema });
export type SupportRatingRequest = z.input<typeof supportRatingRequestSchema>;

export const supportUnreadResponseSchema = z.object({
  items: z.array(z.object({ businessId: uuidSchema, unread: z.number().int() })),
});
export type SupportUnreadResponse = z.infer<typeof supportUnreadResponseSchema>;

// --- Control Center: the Teamna team -------------------------------------------------------

export const adminSupportStatusSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  kind: supportKindSchema,
  color: supportColorSchema,
  position: z.number().int(),
  isDefault: z.boolean(),
});
export type AdminSupportStatus = z.infer<typeof adminSupportStatusSchema>;

/** A label the Teamna team puts on tickets. The words on screen say "label"; the tables say tag. */
export const adminSupportTagSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  color: supportColorSchema,
});
export type AdminSupportTag = z.infer<typeof adminSupportTagSchema>;

/** A Teamna team member who signed in to Control Center: the people a ticket can be assigned to. */
export const adminSupportStaffSchema = z.object({
  email: z.string(),
  name: z.string().nullable(),
});
export type AdminSupportStaff = z.infer<typeof adminSupportStaffSchema>;

export const adminSupportCardSchema = z.object({
  id: uuidSchema,
  number: z.number().int(),
  subject: z.string(),
  statusId: uuidSchema,
  kind: supportKindSchema,
  boardRank: z.number(),
  business: z.object({ id: uuidSchema, name: z.string(), demo: z.boolean() }),
  senderRole: businessRoleSchema,
  urgency: supportUrgencySchema,
  priority: supportPrioritySchema,
  assigneeStaffEmail: z.string().nullable(),
  /** The last word on the thread is the customer's: the team owes an answer. */
  waitingOnTeam: z.boolean(),
  lastCustomerMessageAt: isoDateTimeSchema.nullable(),
  lastActivityAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
  /** The labels on the ticket that are not archived, by name. */
  tags: z.array(adminSupportTagSchema),
});
export type AdminSupportCard = z.infer<typeof adminSupportCardSchema>;

export const adminSupportBoardSchema = z.object({
  statuses: z.array(adminSupportStatusSchema),
  tickets: z.array(adminSupportCardSchema),
  /** Every label that is not archived, by name, for the pickers and the filters. */
  tags: z.array(adminSupportTagSchema),
  /** The team members a ticket can be assigned to, by name. */
  staff: z.array(adminSupportStaffSchema),
});
export type AdminSupportBoard = z.infer<typeof adminSupportBoardSchema>;

export const adminSupportMessageSchema = z.object({
  id: uuidSchema,
  author: z.enum(["customer", "team", "system"]),
  authorName: z.string().nullable(),
  authorStaffEmail: z.string().nullable(),
  internal: z.boolean(),
  body: z.string(),
  createdAt: isoDateTimeSchema,
  files: z.array(supportFileSchema),
});
export type AdminSupportMessage = z.infer<typeof adminSupportMessageSchema>;

export const adminSupportTicketSchema = adminSupportCardSchema.extend({
  context: z.record(z.string(), z.string()),
  sender: z.object({
    userId: uuidSchema.nullable(),
    name: z.string().nullable(),
    phoneE164: z.string().nullable(),
    email: z.string().nullable(),
  }),
  businessCard: z.object({
    id: uuidSchema,
    name: z.string(),
    ownerName: z.string().nullable(),
    ownerPhoneE164: z.string().nullable(),
    ownerEmail: z.string().nullable(),
    billingState: z.string().nullable(),
    demo: z.boolean(),
  }),
  rating: supportRatingSchema.nullable(),
  messages: z.array(adminSupportMessageSchema),
});
export type AdminSupportTicket = z.infer<typeof adminSupportTicketSchema>;

export const adminSupportReplyRequestSchema = z
  .object({
    body: bodySchema,
    /** A team note the sender never sees. */
    internal: z.boolean().default(false),
    /** Move the ticket in the same action ("Send and move to"). */
    moveToStatusId: uuidSchema.optional(),
    /**
     * The kind of that column as the screen shows it; required with `moveToStatusId`. Another kind
     * now is a 409: the column changed after the screen loaded.
     */
    moveToKind: supportKindSchema.optional(),
  })
  .refine((value) => value.moveToStatusId === undefined || value.moveToKind !== undefined, {
    message: "Send the kind of the column with the move.",
    path: ["moveToKind"],
  });
export type AdminSupportReplyRequest = z.input<typeof adminSupportReplyRequestSchema>;

export const adminSupportMoveRequestSchema = z.object({
  statusId: uuidSchema,
  /** Put the card right under this one; null puts it at the top of the column. */
  afterTicketId: uuidSchema.nullable().default(null),
  /**
   * The kind of the column as the screen shows it. Required: another kind now is a 409, so a screen
   * from before a kind change in the settings never moves a ticket (internal review F1.1, F2.1).
   */
  expectedKind: supportKindSchema,
});
export type AdminSupportMoveRequest = z.input<typeof adminSupportMoveRequestSchema>;

/** GET /v1/admin/support/tickets: the list view. Search on the server, filters in the browser. */
export const adminSupportListQuerySchema = z.object({
  /** The ticket number (with or without #), words of the subject, of any message (notes too) or of the business name. */
  q: z.string().trim().max(200).optional(),
});
export type AdminSupportListQuery = z.input<typeof adminSupportListQuerySchema>;

export const adminSupportListSchema = z.object({
  tickets: z.array(adminSupportCardSchema),
  /** True when more tickets match than the list returns (`SUPPORT_LIMITS.listRows`). */
  truncated: z.boolean(),
});
export type AdminSupportList = z.infer<typeof adminSupportListSchema>;

const statusNameSchema = z.string().trim().min(1).max(SUPPORT_LIMITS.statusName);

export const adminSupportStatusCreateSchema = z.object({
  name: statusNameSchema,
  kind: supportKindSchema,
  color: supportColorSchema.default("slate"),
});
export type AdminSupportStatusCreate = z.input<typeof adminSupportStatusCreateSchema>;

export const adminSupportStatusUpdateSchema = z
  .object({
    name: statusNameSchema.optional(),
    color: supportColorSchema.optional(),
    /** Only while the column holds no tickets, and not for the default column of its kind. */
    kind: supportKindSchema.optional(),
    /** Make this the default column of its kind. The old default stays as a normal column. */
    isDefault: z.literal(true).optional(),
    /** Only while the column holds no tickets, and not for the default column of its kind. */
    archived: z.literal(true).optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined || value.color !== undefined || value.kind !== undefined || value.isDefault !== undefined || value.archived !== undefined,
    { message: "Change the name, the colour, the kind or the default, or archive the column." },
  );
export type AdminSupportStatusUpdate = z.input<typeof adminSupportStatusUpdateSchema>;

/** PUT /v1/admin/support/statuses/order: every column that is not archived, in the new order. */
export const adminSupportStatusOrderSchema = z.object({
  statusIds: z.array(uuidSchema).min(1).max(SUPPORT_LIMITS.statuses),
});
export type AdminSupportStatusOrder = z.input<typeof adminSupportStatusOrderSchema>;

export const adminSupportStatusesSchema = z.object({ statuses: z.array(adminSupportStatusSchema) });
export type AdminSupportStatuses = z.infer<typeof adminSupportStatusesSchema>;

/** PATCH /v1/admin/support/tickets/:id: the team member and the team priority. */
export const adminSupportTicketUpdateSchema = z
  .object({
    /** A team member who signed in to Control Center, or null for nobody. */
    assigneeStaffEmail: z.string().trim().email().nullable().optional(),
    priority: supportPrioritySchema.optional(),
  })
  .refine((value) => value.assigneeStaffEmail !== undefined || value.priority !== undefined, {
    message: "Change the team member or the priority.",
  });
export type AdminSupportTicketUpdate = z.input<typeof adminSupportTicketUpdateSchema>;

/** A saved reply: text the team puts in the composer and changes before it sends. */
export const adminSupportSavedReplySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  body: z.string(),
  updatedAt: isoDateTimeSchema,
});
export type AdminSupportSavedReply = z.infer<typeof adminSupportSavedReplySchema>;

export const adminSupportSavedRepliesSchema = z.object({ items: z.array(adminSupportSavedReplySchema) });
export type AdminSupportSavedReplies = z.infer<typeof adminSupportSavedRepliesSchema>;

const savedReplyTitleSchema = z.string().trim().min(1).max(SUPPORT_LIMITS.savedReplyTitle);

export const adminSupportSavedReplyCreateSchema = z.object({ title: savedReplyTitleSchema, body: bodySchema });
export type AdminSupportSavedReplyCreate = z.input<typeof adminSupportSavedReplyCreateSchema>;

export const adminSupportSavedReplyUpdateSchema = z
  .object({ title: savedReplyTitleSchema.optional(), body: bodySchema.optional() })
  .refine((value) => value.title !== undefined || value.body !== undefined, { message: "Change the title or the text." });
export type AdminSupportSavedReplyUpdate = z.input<typeof adminSupportSavedReplyUpdateSchema>;

const tagNameSchema = z.string().trim().min(1).max(SUPPORT_LIMITS.tagName);

export const adminSupportTagCreateSchema = z.object({
  name: tagNameSchema,
  color: supportColorSchema.default("slate"),
});
export type AdminSupportTagCreate = z.input<typeof adminSupportTagCreateSchema>;

export const adminSupportTagUpdateSchema = z
  .object({
    name: tagNameSchema.optional(),
    color: supportColorSchema.optional(),
    /** Archive the label: it leaves every ticket and every picker. */
    archived: z.literal(true).optional(),
  })
  .refine((value) => value.name !== undefined || value.color !== undefined || value.archived !== undefined, {
    message: "Change the name, the colour, or archive the label.",
  });
export type AdminSupportTagUpdate = z.input<typeof adminSupportTagUpdateSchema>;

export const adminSupportTicketTagsSchema = z.object({
  /** Every label the ticket has after the change. */
  tagIds: z.array(uuidSchema).max(20),
});
export type AdminSupportTicketTags = z.input<typeof adminSupportTicketTagsSchema>;
